import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collectionGroup, doc, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { listAccountsForMemberships, listUserAccounts, selectCurrentAccount } from '../services/accountService';

const orderAccounts = (uid, accounts) => [...accounts].sort((left, right) => {
  const leftPersonal = left.ownerUid === uid || left.type === 'personal';
  const rightPersonal = right.ownerUid === uid || right.type === 'personal';
  if (leftPersonal !== rightPersonal) return leftPersonal ? -1 : 1;
  return String(left.name || '').localeCompare(String(right.name || ''), 'pt-BR');
});

export const useUserAccounts = (uid, { enabled = true } = {}) => {
  const [accounts, setAccounts] = useState([]);
  const [currentAccountId, setCurrentAccountId] = useState(null);
  const [loading, setLoading] = useState(Boolean(uid && enabled));
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(uid && enabled ? 'resolving' : 'disabled');
  const [selectingFallback, setSelectingFallback] = useState(false);
  const generationRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!uid || !enabled) return [];
    setLoading(true);
    setStatus('resolving');
    try {
      const nextAccounts = orderAccounts(uid, await listUserAccounts(uid));
      setAccounts(nextAccounts);
      setError(null);
      setStatus('ready');
      return nextAccounts;
    } catch (nextError) {
      setError(nextError);
      setStatus('error');
      throw nextError;
    } finally {
      setLoading(false);
    }
  }, [uid, enabled]);

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    let active = true;
    let membershipRevision = 0;
    if (!uid || !enabled) {
      setAccounts([]);
      setCurrentAccountId(null);
      setLoading(false);
      setError(null);
      setStatus('disabled');
      return undefined;
    }
    setAccounts([]);
    setCurrentAccountId(null);
    setLoading(true);
    setError(null);
    setStatus('resolving');
    const stopProfile = onSnapshot(doc(firestore, 'users', uid), (snapshot) => {
      if (!active || generationRef.current !== generation) return;
      setCurrentAccountId(snapshot.data()?.currentAccountId || null);
    }, (nextError) => {
      if (!active || generationRef.current !== generation) return;
      setError(nextError);
      setStatus('error');
      setLoading(false);
    });
    const membershipQuery = query(collectionGroup(firestore, 'members'), where('uid', '==', uid));
    const stopMemberships = onSnapshot(membershipQuery, (snapshot) => {
      if (!active || generationRef.current !== generation) return;
      const revision = ++membershipRevision;
      const memberships = snapshot.docs.map((item) => item.data()).filter((item) => item?.uid === uid && item?.accountId);
      const byAccountId = new Map(memberships.map((membership) => [membership.accountId, membership]));
      setAccounts((current) => orderAccounts(uid, current
        .filter((account) => byAccountId.has(account.id))
        .map((account) => ({ ...account, membership: byAccountId.get(account.id), role: byAccountId.get(account.id).role }))));
      setLoading(true);
      setStatus('resolving');
      Promise.resolve(listAccountsForMemberships(memberships))
        .then((nextAccounts) => {
          if (!active || generationRef.current !== generation || revision !== membershipRevision) return;
          setAccounts(orderAccounts(uid, nextAccounts));
          setError(null);
          setStatus('ready');
          setLoading(false);
        })
        .catch((nextError) => {
          if (!active || generationRef.current !== generation || revision !== membershipRevision) return;
          setError(nextError);
          setStatus('error');
          setLoading(false);
        });
    }, (nextError) => {
      if (!active || generationRef.current !== generation) return;
      setError(nextError);
      setStatus('error');
      setLoading(false);
    });
    return () => {
      active = false;
      if (generationRef.current === generation) generationRef.current += 1;
      stopProfile();
      stopMemberships();
    };
  }, [uid, enabled]);

  useEffect(() => {
    if (loading || !uid || !accounts.length) return;
    if (accounts.some((account) => account.id === currentAccountId)) return;
    const fallback = accounts.find((account) => account.ownerUid === uid) || accounts[0];
    setSelectingFallback(true);
    selectCurrentAccount(uid, fallback.id)
      .then((selected) => setCurrentAccountId(selected.accountId))
      .catch(setError)
      .finally(() => setSelectingFallback(false));
  }, [accounts, currentAccountId, loading, uid]);

  const selectAccount = useCallback(async (accountId) => {
    if (!accounts.some((account) => account.id === accountId)) throw new Error('ACCOUNT_ACCESS_DENIED');
    const selected = await selectCurrentAccount(uid, accountId);
    setCurrentAccountId(selected.accountId);
    return selected;
  }, [accounts, uid]);

  const currentAccount = useMemo(
    () => accounts.find((account) => account.id === currentAccountId) || null,
    [accounts, currentAccountId]
  );

  return { accounts, currentAccountId, currentAccount, loading, error, status, selectingFallback, selectAccount, refresh };
};

export default useUserAccounts;
